"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.retryPermissionUpdate = exports.fetchInstance = exports.shopDomain = void 0;
exports.setApiAppBridge = setApiAppBridge;
const apiUrl = process.env.REACT_APP_API_BASE_URL;
exports.shopDomain = undefined;
// getSessionToken() does a full postMessage round-trip to the parent Admin
// frame every time it's called, with no built-in sharing — App.js firing
// several requests together on mount (shop info, permissions, dashboard
// report, has-any-orders) triggered that many independent round-trips in
// parallel instead of one, which is most of why parallelizing those requests
// barely moved the dashboard's LCP elementRenderDelay (~2469ms -> ~2400ms).
// Mirrors the same pattern already used by the OTHER (older, axios-based)
// HTTP layer in api/index.js's _getSessionTokenShared() — that one just never
// covered this Orval/fetchInstance layer, which is what shop info,
// permissions, and both dashboard calls actually go through. A token is
// valid for ~1 minute per Shopify, so a short reuse window is safe.
let sessionTokenPromise;
let sessionTokenFetchedAt = 0;
function getSessionTokenShared() {
    return __awaiter(this, void 0, void 0, function* () {
        const now = Date.now();
        if (sessionTokenPromise && now - sessionTokenFetchedAt < 5000) {
            return sessionTokenPromise;
        }
        sessionTokenFetchedAt = now;
        const v4 = window.shopify;
        sessionTokenPromise = v4.idToken().catch((err) => {
            sessionTokenPromise = undefined;
            throw err;
        });
        return sessionTokenPromise;
    });
}
/**
 * IMPORTANT:
 * Every time you add/remove scopes, increase this version.
 * Example: v1 -> v2
 */
// 2026-07 migration: found out of sync with Prime-Extensions/shopify.app.toml's
// [access_scopes] scopes — that list has 2 scopes this one didn't:
// write_merchant_managed_fulfillment_orders and read_locales. Without them
// here, a merchant who re-authorizes via THIS manual redirect flow (as
// opposed to a fresh managed install) would only ever be asked to grant the
// narrower, stale scope set. Brought back in sync; bumped per the rule above.
const SCOPES_VERSION = 'v5';
const REQUIRED_SCOPES = 'write_products,read_products,write_orders,read_price_rules,write_price_rules,read_discounts,write_discounts,read_themes,read_customers,write_customers,read_markets,read_cart_transforms,write_cart_transforms,write_merchant_managed_fulfillment_orders,read_locales';
const getReauthKey = (shop) => `reauth_status_${shop || 'unknown-shop'}_${SCOPES_VERSION}`;
const getReauthStatus = (shop) => {
    const key = getReauthKey(shop);
    return localStorage.getItem(key);
};
const setReauthStatus = (status, shop) => {
    const key = getReauthKey(shop);
    localStorage.setItem(key, status);
    // console.log('[fetchInstance] reauth status updated ->', {
    //   key,
    //   status,
    // });
};
const clearReauthStatus = (shop) => {
    const key = getReauthKey(shop);
    localStorage.removeItem(key);
    // console.log('[fetchInstance] reauth status cleared ->', {
    //   key,
    // });
};
const buildPermissionUrl = (shop) => {
    const redirectUri = `${apiUrl}/api/offline/auth`;
    const state = String(Date.now());
    const permissionUrl = `https://${shop}/admin/oauth/authorize?` +
        `client_id=${process.env.REACT_APP_API_KEY}&` +
        `scope=${encodeURIComponent(REQUIRED_SCOPES)}&` +
        `redirect_uri=${encodeURIComponent(redirectUri)}&` +
        `state=${state}&` +
        `grant_options[]=`;
    // console.log('[fetchInstance] buildPermissionUrl ->', {
    //   shop,
    //   redirectUri,
    //   state,
    //   permissionUrl,
    //   scopesVersion: SCOPES_VERSION,
    //   scopes: REQUIRED_SCOPES,
    // });
    return permissionUrl;
};
const redirectToStoreAdmin = () => {
    if (!exports.shopDomain) {
        console.warn('[fetchInstance] redirectToStoreAdmin skipped -> shopDomain missing');
        return;
    }
    // Mirror what the backend afterAuth handler does on a successful OAuth
    // round-trip: hop the TOP window back to the embedded app URL. We
    // deliberately do NOT navigate to admin.shopify.com/store/<name> any more.
    // That URL ships X-Frame-Options: DENY, so:
    //   • assigning window.location.href to it leaves the iframe stuck on a
    //     "refused to connect" page (the original Mac spin bug);
    //   • assigning window.top.location.href to it boots the merchant out of
    //     the app entirely (a worse regression, observed in production).
    // The app URL (https://{shop}/admin/apps/{handle}) is iframe-friendly —
    // Shopify wraps it back into the embedded admin shell — so the merchant
    // simply gets a fresh load of our app and stays exactly where they were.
    const appHandle = process.env.REACT_APP_SHOPIFY_APP_SLUG || '';
    const appUrl = `https://${exports.shopDomain}/admin/apps/${appHandle}`;
    try {
        if (window.top && window.top !== window.self) {
            window.top.location.href = appUrl;
            return;
        }
    }
    catch (e) {
        // window.top access denied (cross-origin) — fall through to same-frame.
    }
    window.location.href = appUrl;
};
const isLikelyCorsOrRedirectFailure = (error) => {
    if (!(error instanceof Error))
        return false;
    const message = error.message || '';
    const result = error instanceof TypeError ||
        message.includes('Failed to fetch') ||
        message.includes('NetworkError') ||
        message.includes('Load failed');
    // console.log('[fetchInstance] isLikelyCorsOrRedirectFailure ->', {
    //   message,
    //   result,
    //   error,
    // });
    return result;
};
const triggerReauthorization = (manual = false) => {
    const reauthKey = getReauthKey(exports.shopDomain);
    const reauthStatus = localStorage.getItem(reauthKey);
    // console.log('[fetchInstance] triggerReauthorization called ->', {
    //   shopDomain,
    //   reauthKey,
    //   reauthStatus,
    //   apiUrl,
    //   scopesVersion: SCOPES_VERSION,
    //   manual,
    // });
    if (!manual && (reauthStatus === 'pending' || reauthStatus === 'verified')) {
        // console.log('[fetchInstance] reauth already handled, skipping redirect ->', {
        //   reauthKey,
        //   reauthStatus,
        //   manual,
        // });
        throw new Error('REAUTH_ALREADY_HANDLED');
    }
    if (!exports.shopDomain) {
        // console.error('[fetchInstance] shopDomain is missing');
        throw new Error('SHOP_DOMAIN_MISSING');
    }
    const permissionUrl = buildPermissionUrl(exports.shopDomain);
    setReauthStatus('pending', exports.shopDomain);
    // console.log('[fetchInstance] localStorage set pending ->', {
    //   reauthKey,
    //   value: localStorage.getItem(reauthKey),
    // });
    // 2026-07 migration: was Redirect.create(shopifyApp).dispatch(Redirect.Action.REMOTE, permissionUrl)
    // (App Bridge v3). Same top-window navigation redirectToStoreAdmin() above
    // already does without any App Bridge instance at all.
    try {
        if (window.top && window.top !== window.self) {
            window.top.location.href = permissionUrl;
        }
        else {
            window.location.href = permissionUrl;
        }
    }
    catch (e) {
        // window.top access denied (cross-origin) — fall through to same-frame.
        window.location.href = permissionUrl;
    }
    throw new Error('REAUTH_TRIGGERED');
};
function setApiAppBridge(data) {
    return __awaiter(this, void 0, void 0, function* () {
        // console.log('[setApiAppBridge] called ->', data);
        // 2026-07 migration: used to create/reuse a v3 App Bridge instance here
        // (guarded so only the first call created one). Removed along with v3 —
        // there is nothing left to initialize but shopDomain, so every call just
        // updates it; safe to call more than once.
        exports.shopDomain = data.shopDomain;
    });
}
const fetchInstance = (options) => __awaiter(void 0, void 0, void 0, function* () {
    return fetchInstanceAttempt(options, false);
});
exports.fetchInstance = fetchInstance;
// isRetry is internal-only (not part of the public signature Orval's generated
// clients call with) — see the retry-before-reauth comment in the catch block.
const fetchInstanceAttempt = (_a, isRetry_1) => __awaiter(void 0, [_a, isRetry_1], void 0, function* ({ url, method, params, data, headers, responseType }, isRetry) {
    var _b;
    const reauthKey = getReauthKey(exports.shopDomain);
    const t0 = performance.now();
    try {
        const hasV4Global = !!((_b = window.shopify) === null || _b === void 0 ? void 0 : _b.idToken);
        const baseUrl = `${apiUrl}${url}`;
        // `new URLSearchParams(params)` stringifies EVERY value, including
        // `undefined`/`null`, into the literal text "undefined"/"null" — a real
        // bug, not a hypothetical one: an orval-generated GET call built from an
        // object with an omitted optional field (e.g. `{ byCampaignId: undefined,
        // cursor, limit }`, the normal shape for "no filter") silently sent
        // `byCampaignId=undefined` on the wire, which the backend then treated as
        // an actual (non-matching) filter value instead of "omitted". Strip
        // nullish entries first so an omitted param stays omitted.
        const cleanParams = params && typeof params === 'object'
            ? Object.keys(params).reduce((acc, key) => {
                if (params[key] != null)
                    acc[key] = params[key];
                return acc;
            }, {})
            : params;
        const finalUrl = cleanParams && Object.keys(cleanParams).length ? `${baseUrl}?${new URLSearchParams(cleanParams)}` : baseUrl;
        let token;
        // 2026-07 migration: was `if (!!shopifyApp)` — same guard, now against
        // the v4 global instead of a v3 app-bridge instance. The v4 CDN script
        // is only injected when embedded (see public/index.html), so this can
        // still legitimately be false on a non-embedded page.
        if (hasV4Global) {
            token = yield getSessionTokenShared();
        }
        else {
            console.warn('[fetchInstance] App Bridge v4 global not present before getSessionToken ->', url);
        }
        if (token == undefined && !finalUrl.includes('isInstalled') && !finalUrl.includes('plans') && !finalUrl.includes('storage')) {
            // console.error('[fetchInstance] SESSION_TOKEN_MISSING');
            throw new Error('SESSION_TOKEN_MISSING');
        }
        const response = yield fetch(finalUrl, Object.assign(Object.assign({ method, redirect: 'manual' }, (data ? { body: JSON.stringify(data) } : {})), { headers: Object.assign(Object.assign({ 'Content-Type': 'application/json' }, (headers || {})), { Authorization: !!token ? `Bearer ${token}` : '', ShopDomain: exports.shopDomain || '', 'ngrok-skip-browser-warning': 'true' }) }));
        /**
         * Case 1:
         * backend returned redirect to auth/install and browser exposed it as opaqueredirect / status 0
         */
        if (response.type === 'opaqueredirect' || response.status === 0) {
            console.warn(`[fetchInstance] opaqueredirect/status0 at ${(performance.now() - t0).toFixed(0)}ms -> reauth check`, { type: response.type, status: response.status, finalUrl });
            const currentStatus = getReauthStatus(exports.shopDomain);
            // User went to permission update flow and came back without approval
            if (currentStatus === 'pending') {
                // console.warn('[fetchInstance] user likely cancelled permission update -> clear status and go to admin', {
                //   currentStatus,
                //   finalUrl,
                // });
                clearReauthStatus(exports.shopDomain);
                redirectToStoreAdmin();
                throw new Error('REAUTH_CANCELLED');
            }
            triggerReauthorization(false);
        }
        /**
         * Case 2:
         * backend returned real 403
         */
        if (response.status === 403) {
            console.warn(`[fetchInstance] 403 at ${(performance.now() - t0).toFixed(0)}ms -> reauth check`, { type: response.type, status: response.status, finalUrl });
            const currentStatus = getReauthStatus(exports.shopDomain);
            if (currentStatus === 'pending') {
                // console.warn('[fetchInstance] permission still missing after pending reauth -> clear status and go to admin', {
                //   currentStatus,
                //   finalUrl,
                // });
                clearReauthStatus(exports.shopDomain);
                redirectToStoreAdmin();
                throw new Error('REAUTH_CANCELLED');
            }
            triggerReauthorization(false);
        }
        if (!response.ok) {
            let errorText = '';
            try {
                errorText = yield response.text();
            }
            catch (readError) {
                // console.warn('[fetchInstance] could not read error response text ->', readError);
            }
            // console.error('[fetchInstance] non-ok response ->', {
            //   status: response.status,
            //   errorText,
            //   finalUrl,
            // });
            throw new Error(`HTTP_${response.status}${errorText ? `: ${errorText}` : ''}`);
        }
        /**
         * If request succeeded after a pending reauth, mark as verified
         */
        if (localStorage.getItem(reauthKey) === 'pending') {
            setReauthStatus('verified', exports.shopDomain);
            // console.log('[fetchInstance] reauth status changed pending -> verified', {
            //   reauthKey,
            //   value: localStorage.getItem(reauthKey),
            // });
        }
        if (responseType === 'text') {
            const result = (yield response.text());
            // console.log('[fetchInstance] parsed text response ->', result);
            return result;
        }
        if (responseType === 'blob') {
            const result = (yield response.blob());
            // console.log('[fetchInstance] parsed blob response ->', result);
            return result;
        }
        const jsonResult = (yield response.json());
        return jsonResult;
    }
    catch (errors) {
        const reauthStatus = localStorage.getItem(reauthKey);
        if ((errors === null || errors === void 0 ? void 0 : errors.message) === 'REAUTH_CANCELLED') {
            // console.warn('[fetchInstance] stopping flow because user cancelled permission update');
            throw errors;
        }
        if ((errors === null || errors === void 0 ? void 0 : errors.message) === 'REAUTH_ALREADY_HANDLED' && !url.includes('plans') && !url.includes('storage')) {
            // console.warn('[fetchInstance] stopping flow because reauth is already handled');
            throw errors;
        }
        /**
         * Case 3:
         * fetch failed before usable response returned
         * often happens in redirect/cors/network style failures
         */
        if (isLikelyCorsOrRedirectFailure(errors) &&
            reauthStatus !== 'pending' &&
            reauthStatus !== 'verified') {
            // A generic fetch-level failure (esp. Safari's "Load failed", which
            // fires far more readily than Chrome's "Failed to fetch" — first-request
            // timing, ITP-related session-token/cookie delays, transient network
            // blips) is weak evidence of an actual missing scope; a REAL scope
            // problem comes back as an explicit 403/opaqueredirect from the server,
            // handled above, not this catch-all. Retry the same request once before
            // concluding it's a scope problem and forcing a disruptive full OAuth
            // redirect — this was firing (and, since the `reauth_status_*`
            // localStorage flag this guards against re-triggering with doesn't
            // reliably survive Safari's third-party-iframe storage partitioning
            // across the OAuth redirect-and-reembed round trip, sometimes firing
            // TWICE) on nothing more than a flaky first request.
            if (!isRetry) {
                console.warn(`[fetchInstance] likely CORS/redirect failure at ${(performance.now() - t0).toFixed(0)}ms -> RETRYING ONCE`, { url, reauthStatus });
                return fetchInstanceAttempt({ url, method, params, data, headers, responseType }, true);
            }
            console.warn(`[fetchInstance] likely CORS/redirect failure again after retry at ${(performance.now() - t0).toFixed(0)}ms -> triggering reauth`, { url, reauthStatus });
            triggerReauthorization(false);
        }
        // If request fails after pending reauth, treat it as cancelled/no approval
        if (isLikelyCorsOrRedirectFailure(errors) && reauthStatus === 'pending') {
            // console.warn('[fetchInstance] likely user cancelled permission update from catch block -> clear status and go to admin', {
            //   reauthKey,
            //   reauthStatus,
            // });
            clearReauthStatus(exports.shopDomain);
            redirectToStoreAdmin();
            throw new Error('REAUTH_CANCELLED');
        }
        throw errors;
    }
});
const retryPermissionUpdate = () => {
    // console.log('[fetchInstance] retryPermissionUpdate called manually');
    const reauthKey = getReauthKey(exports.shopDomain);
    if (exports.shopDomain) {
        localStorage.removeItem(reauthKey);
    }
    triggerReauthorization(true);
};
exports.retryPermissionUpdate = retryPermissionUpdate;
exports.default = exports.fetchInstance;
